'use client';

import { useCallback, useMemo } from 'react';
import { alpha } from '@mui/material/styles';
import { Chip, Stack } from '@/primitives';
import { LAYOUT_VARIANT, type LayoutVariant } from '@/configs/layout';
import {
  DETAIL_ANIMAL_SEARCH_TYPES,
  SEARCH_TYPE,
} from '@/constants/searchType';
import { useSearchType } from '@/hooks/publicSearch/useSearchType';
import type { SearchType } from '@/hooks/publicSearch/useSearchType';
import { useSearch } from '@/hooks/publicSearch/useSearch';
import { useEbvDisplayOptions } from '@/hooks/publicSearch/useEbvDisplayOptions';
import { parseEbvConfig } from '@/hooks/publicSearch/ebv/ebvColumns';
import useFilterStore from '@/publicSearch/store/useFilterStore';
import useGridStore from '@/publicSearch/store/useGridStore';
import useSearchStore from '@/publicSearch/store/useSearchStore';
import usePublicSearchContext from '@/publicSearch/store/usePublicSearchContext';
import { FieldOption, OperatorOption } from '../../constants';
import {
  ANIMAL_LOOKUP_FIELD_PREFIX,
  getAllFieldOptionsForSearchType,
  getOptionsByFieldAndSearchType,
  parseAnimalSearchFieldsSetting,
} from '../constants';
import { isEbvField } from '../ebvFilterFields';
import { parseRangeCompositeValue } from '../rangeValue';

const searchFieldNamesByType: Partial<Record<SearchType, string[]>> = {
  [SEARCH_TYPE.ANIMAL]: [FieldOption.searchTerm.value],
  [SEARCH_TYPE.MEMBERS]: [
    FieldOption.memberIdent.value,
    FieldOption.memberName.value,
  ],
  [SEARCH_TYPE.SALE_CATALOGUES]: [FieldOption.saleName.value],
  [SEARCH_TYPE.SALE_CATALOGUES_DETAIL_ANIMALS]: [FieldOption.searchTerm.value],
  [SEARCH_TYPE.SEMEN_CATALOGUES_DETAIL_ANIMALS]: [FieldOption.searchTerm.value],
};

type ActiveFilterChip = {
  key: string;
  label: string;
  type: 'myHerd' | 'flat' | 'group';
  index?: number;
  groupIndex?: number;
  filterIndex?: number;
};

type ActiveFilterChipsProps = {
  variant: LayoutVariant;
};

function formatCompositeValue(rawValue: string) {
  const parsed = parseRangeCompositeValue(rawValue);
  const parts = [
    parsed?.min ? `Min ${parsed.min}` : '',
    parsed?.max ? `Max ${parsed.max}` : '',
  ].filter(Boolean);
  return parts.length > 0 ? parts.join(' ') : rawValue;
}

export function getEbvRangePrefix(field: string, operator: string) {
  if (!isEbvField(field)) return '';
  if (operator === OperatorOption.greaterThanOrEqual.value) return 'Min ';
  if (operator === OperatorOption.lessThanOrEqual.value) return 'Max ';
  return '';
}

export default function ActiveFilterChips({ variant }: ActiveFilterChipsProps) {
  const searchType = useSearchType();
  const society = usePublicSearchContext((context) => context.society);
  const basicSearchInput = useSearchStore((state) => state.basicSearchInput);
  const { myAnimals, paginationModel, setMyAnimals, setPaginationModel } =
    useGridStore();
  const {
    filters,
    filterGroups,
    removeFilter,
    removeFilterFromGroup,
    removeFilterGroup,
  } = useFilterStore(searchType);
  const { startSearch } = useSearch();
  const { ebvLabel, showPercentileSearch } = useEbvDisplayOptions();

  const ebvConfig = useMemo(
    () => parseEbvConfig(society.settings),
    [society.settings],
  );
  const configuredAnimalSearchFields = useMemo(
    () =>
      searchType === SEARCH_TYPE.ANIMAL ||
      DETAIL_ANIMAL_SEARCH_TYPES.includes(searchType)
        ? parseAnimalSearchFieldsSetting(society.settings)
        : [],
    [searchType, society.settings],
  );

  const { fieldLabelMap, configuredValueLabelMap } = useMemo(() => {
    const fieldLabels = new Map<string, string>();
    const configuredValueLabels = new Map<string, Map<string, string>>();

    Object.values(FieldOption).forEach((field) => {
      fieldLabels.set(field.value, field.label);
    });
    getAllFieldOptionsForSearchType(
      searchType,
      [],
      ebvConfig,
      undefined,
      undefined,
      ebvLabel,
      showPercentileSearch,
    ).forEach((field) => {
      fieldLabels.set(field.value, field.label);
    });
    configuredAnimalSearchFields.forEach((field) => {
      fieldLabels.set(field.value, field.label);
      if (!field.lookups?.length) return;

      const lookupField = `${ANIMAL_LOOKUP_FIELD_PREFIX}${field.key ?? field.value}`;
      const valueLabels = new Map(
        field.lookups.map((lookup) => [lookup.code, lookup.description]),
      );
      fieldLabels.set(lookupField, field.label);
      configuredValueLabels.set(field.value, valueLabels);
      configuredValueLabels.set(lookupField, valueLabels);
    });

    return {
      fieldLabelMap: fieldLabels,
      configuredValueLabelMap: configuredValueLabels,
    };
  }, [
    configuredAnimalSearchFields,
    ebvConfig,
    searchType,
    ebvLabel,
    showPercentileSearch,
  ]);

  const formatFilterChipLabel = useCallback(
    (field: string, operator: string, value: string) => {
      const fieldLabel = fieldLabelMap.get(field) ?? field.replace(/_/g, ' ');
      if (operator === OperatorOption.isNotEmpty.value || !value.trim()) {
        return fieldLabel;
      }
      const optionLabel =
        configuredValueLabelMap.get(field)?.get(value) ??
        getOptionsByFieldAndSearchType(field, searchType).find(
          (option) => option.value === value,
        )?.label ??
        formatCompositeValue(value);
      return `${fieldLabel}: ${getEbvRangePrefix(field, operator)}${optionLabel}`;
    },
    [configuredValueLabelMap, fieldLabelMap, searchType],
  );

  const activeFilterChips = useMemo<ActiveFilterChip[]>(() => {
    const next: ActiveFilterChip[] = [];
    const trimmedSearch = basicSearchInput.trim();
    const searchFieldNames = new Set(searchFieldNamesByType[searchType] ?? []);

    if (searchType === SEARCH_TYPE.ANIMAL && myAnimals) {
      next.push({ key: 'my-herd', label: 'My Herd', type: 'myHerd' });
    }

    filters.forEach((filter, index) => {
      if (!filter.field || (!filter.operator && !filter.value)) return;
      if (
        trimmedSearch &&
        searchFieldNames.has(filter.field) &&
        filter.value === trimmedSearch
      ) {
        return;
      }
      next.push({
        key: `flat-${index}-${filter.field}-${filter.value}`,
        label: formatFilterChipLabel(
          filter.field,
          filter.operator,
          filter.value,
        ),
        type: 'flat',
        index,
      });
    });

    filterGroups.forEach((group, groupIndex) => {
      group.filters.forEach((filter, filterIndex) => {
        if (!filter.field || (!filter.operator && !filter.value)) return;
        next.push({
          key: `group-${groupIndex}-${filterIndex}-${filter.field}-${filter.value}`,
          label: formatFilterChipLabel(
            filter.field,
            filter.operator,
            filter.value,
          ),
          type: 'group',
          groupIndex,
          filterIndex,
        });
      });
    });

    return next;
  }, [
    basicSearchInput,
    filterGroups,
    filters,
    formatFilterChipLabel,
    myAnimals,
    searchType,
  ]);

  const handleDelete = (chip: ActiveFilterChip) => {
    setPaginationModel({ ...paginationModel, page: 0 });

    if (chip.type === 'myHerd') setMyAnimals(false);
    if (chip.type === 'flat' && typeof chip.index === 'number') {
      removeFilter(chip.index);
    }
    if (
      chip.type === 'group' &&
      typeof chip.groupIndex === 'number' &&
      typeof chip.filterIndex === 'number'
    ) {
      const group = filterGroups[chip.groupIndex];
      if (!group) return;
      if (group.filters.length <= 1) {
        removeFilterGroup(chip.groupIndex);
      } else {
        removeFilterFromGroup(chip.groupIndex, chip.filterIndex);
      }
    }

    // Defer the search until after the store mutations above have flushed,
    // so startSearch reads the updated filters rather than this render's stale set.
    setTimeout(startSearch, 0);
  };

  // TODO: align filter chips for all searches
  if (
    activeFilterChips.length === 0 ||
    (variant === LAYOUT_VARIANT.DESKTOP && searchType !== SEARCH_TYPE.ANIMAL)
  ) {
    return null;
  }

  return (
    <Stack
      direction="row"
      useFlexGap
      spacing={0.75}
      sx={{
        flexWrap: 'wrap',
        alignItems: 'center',
        ...(variant === LAYOUT_VARIANT.MOBILE
          ? { px: 1.5, py: 1 }
          : { px: '10px', pt: 1 }),
      }}
    >
      {activeFilterChips.map((chip) => (
        <Chip
          key={chip.key}
          label={chip.label}
          onDelete={() => handleDelete(chip)}
          sx={{
            height: 28,
            borderRadius: '999px',
            px: 0.25,
            fontSize: '12px',
            color: 'info.main',
            backgroundColor: (theme) => alpha(theme.palette.info.main, 0.12),
            '& .MuiChip-label': { px: 1.25 },
            '& .MuiChip-deleteIcon': {
              fontSize: 14,
              color: 'info.main',
              mr: 0.75,
              ml: -0.5,
            },
          }}
        />
      ))}
    </Stack>
  );
}
